import axios from "axios";

// Same-origin default ("/api") keeps the app functional behind a Vercel
// /api rewrite and can never fall back to a third-party preview backend.
// Set REACT_APP_BACKEND_URL for direct cross-origin deployments.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
if (!process.env.REACT_APP_BACKEND_URL && typeof console !== "undefined") {
  console.warn("[osteon] REACT_APP_BACKEND_URL is not set; using same-origin /api.");
}
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const csrfTokens = {
  user: null,
  admin: null,
};

const setCsrfToken = (scope, token) => {
  csrfTokens[scope] = typeof token === "string" ? token : null;
};

const csrfHeaders = (scope) => {
  const token = csrfTokens[scope];
  return token ? { "X-CSRF-Token": token } : {};
};

const requestWithCsrf = (scope, method, path, data) =>
  api.request({
    method,
    url: path,
    data,
    headers: csrfHeaders(scope),
  }).then((response) => response.data);

export const getCurrentUser = () => api.get("/auth/me").then((response) => response.data);
export const setUserCsrfToken = (token) => setCsrfToken("user", token);

// H2: minimal 429 surfacing. The backend answers 429 + Retry-After with a
// generic detail; map it to one friendly string so users see
// "Too many attempts. Please wait and try again." instead of a confusing
// generic failure. Never surfaces thresholds or limiter internals.
export const RATE_LIMIT_MESSAGE = "Too many attempts. Please wait and try again.";
export const isRateLimited = (err) => err?.response?.status === 429;
// H5: 413 maps to a generic oversized-request message (admin upload
// callers override with "File is too large."). Never exposes internals.
export const isPayloadTooLarge = (err) => err?.response?.status === 413;
export const friendlyApiError = (err, fallback) => {
  if (isRateLimited(err)) return RATE_LIMIT_MESSAGE;
  if (isPayloadTooLarge(err)) return "Request is too large.";
  const detail = err?.response?.data?.detail;
  // FastAPI serializes HTTPException detail as a string, but a Pydantic
  // validation error (and any structured payload) arrives as an object;
  // unwrap it so a toast never renders "[object Object]".
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string" && detail.message) return detail.message;
    if (typeof detail.code === "string" && detail.code) return detail.code;
  }
  return fallback;
};

// Admin sign-in error mapping. 401 stays a single generic string (never
// reveals which factor failed). 429/503 keep their safe messages. A missing
// response (network/CORS) or a 5xx gets its own message so a transport or
// server failure is not misread as bad credentials.
export const adminLoginErrorMessage = (err) => {
  if (isRateLimited(err)) return RATE_LIMIT_MESSAGE;
  const status = err?.response?.status;
  if (status === 503) return "Admin sign-in is not available right now.";
  if (status === 401) return "Invalid credentials";
  if (!err?.response) return "Cannot reach the server. Check your connection and try again.";
  if (status >= 500) return "Server error. Please try again shortly.";
  return "Sign-in failed. Please try again.";
};

// Login failure codes returned by POST /api/admin/login. The UI never
// prints a factor (the toast stays generic); the code only tells the form
// which field to clear.
export const ADMIN_LOGIN_CODE_PRIMARY = "invalid_primary_credentials";
export const ADMIN_LOGIN_CODE_TOTP = "invalid_totp";
export const adminLoginErrorCode = (err) => err?.response?.data?.code || null;
export const isInvalidTotpError = (err) =>
  adminLoginErrorCode(err) === ADMIN_LOGIN_CODE_TOTP;

// Admin data-loading failure classification. The dashboard must react to
// the CAUSE, not to "any rejection": only a real 401 means the session is
// gone. Network drops, rate limits and 5xx must keep the admin signed in
// with a truthful message instead of a false "Session expired" logout.
export const ADMIN_SESSION_EXPIRED_MESSAGE = "Session expired. Please sign in again.";
export const ADMIN_UNREACHABLE_MESSAGE =
  "Cannot reach the server. Check your connection and try again.";
export const ADMIN_SERVER_ERROR_MESSAGE = "Server error. Please try again shortly.";
export const ADMIN_DATA_ERROR_MESSAGE = "Could not load admin data. Please try again.";

export const adminRequestOutcome = (err) => {
  if (isRateLimited(err)) return "rate_limited";
  if (!err?.response) return "network";
  const status = err.response.status;
  if (status === 401) return "session_expired";
  if (status === 403) return "forbidden";
  if (status === 503 || status >= 500) return "server";
  return "error";
};

export const isAdminSessionExpired = (err) =>
  adminRequestOutcome(err) === "session_expired";

export const adminDataError = (err) => {
  const outcome = adminRequestOutcome(err);
  const message =
    outcome === "session_expired"
      ? ADMIN_SESSION_EXPIRED_MESSAGE
      : outcome === "rate_limited"
        ? RATE_LIMIT_MESSAGE
        : outcome === "network"
          ? ADMIN_UNREACHABLE_MESSAGE
          : outcome === "server"
            ? ADMIN_SERVER_ERROR_MESSAGE
            : ADMIN_DATA_ERROR_MESSAGE;
  return { outcome, message, sessionExpired: outcome === "session_expired" };
};

// Sign in with X (OAuth 2.0 + PKCE). The backend returns an X authorization
// URL; the browser performs a full redirect so HttpOnly cookies travel.
// mode is "login" (new/returning account) or "link" (attach X to the
// current wallet session; requires CSRF header, sent when logged in).
export const getXAuthorizationUrl = (mode = "login", ref = null) => {
  const params = new URLSearchParams({ mode });
  if (ref) params.set("ref", ref);
  return requestWithCsrf("user", "get", `/auth/x/start?${params.toString()}`);
};

export const beginXSignIn = async (mode = "login", ref = null) => {
  const data = await getXAuthorizationUrl(mode, ref);
  window.location.assign(data.authorization_url);
  return data;
};

export const logoutUser = () =>
  requestWithCsrf("user", "post", "/auth/logout").finally(() => setCsrfToken("user", null));

// Account wallet: a plain address attached to the authenticated X/Osteon
// account (profile/destination field only). Saved via an authenticated
// session + CSRF; never used as authentication.
export const saveAccountWallet = (walletAddress) =>
  requestWithCsrf("user", "put", "/account/wallet", { wallet_address: walletAddress });

export const removeAccountWallet = () =>
  requestWithCsrf("user", "delete", "/account/wallet");

export const getUser = () => api.get("/user").then((response) => response.data);
// Social tasks are server-authoritative: progress always loads from the
// backend, and completion requires the start/wait/verify sequence.
export const getTaskState = () => api.get("/tasks/state").then((response) => response.data);
export const startTask = (taskId) =>
  requestWithCsrf("user", "post", "/tasks/start", { task_id: taskId });
export const verifyTask = (taskId) =>
  requestWithCsrf("user", "post", "/tasks/verify", { task_id: taskId });
export const getQuestions = () => api.get("/questions").then((response) => response.data);
export const submitTest = (payload) => requestWithCsrf("user", "post", "/test/submit", payload);
export const getLeaderboard = () => api.get("/leaderboard").then((response) => response.data);
export const getReferrals = () => api.get("/referrals").then((response) => response.data);
export const getArchive = () => api.get("/archive").then((response) => response.data);

// S2: admin sign-in requires username + password + TOTP code.
export const adminLogin = (username, password, totpCode) =>
  api.post("/admin/login", { username, password, totp_code: totpCode }).then((response) => {
    setCsrfToken("admin", response.data.csrf_token);
    return response.data;
  });

export const adminGetCurrentSession = () => api.get("/admin/me").then((response) => response.data);
export const setAdminCsrfToken = (token) => setCsrfToken("admin", token);

export const adminLogout = () =>
  requestWithCsrf("admin", "post", "/admin/logout").finally(() => setCsrfToken("admin", null));

export const adminGetUsers = (limit = 100, offset = 0) =>
  api.get(`/admin/users?limit=${limit}&offset=${offset}`).then((response) => response.data);
export const adminGetQuestions = () => api.get("/admin/questions").then((response) => response.data);
export const adminAddQuestion = (question) => requestWithCsrf("admin", "post", "/admin/questions", question);
export const adminDeleteQuestion = (questionId) => requestWithCsrf("admin", "delete", `/admin/questions/${questionId}`);
export const adminUploadQuestionImage = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/admin/questions/upload", form, { headers: csrfHeaders("admin") })
    .then((response) => response.data);
};
export const adminAdjustUser = (userId, grant) =>
  requestWithCsrf("admin", "post", `/admin/users/${userId}/adjust`, grant);

// Pre-launch moderation powers (admin session + CSRF on every call):
// launch reset, per-row leaderboard visibility, and ban/unban. All four
// return the server payload unchanged so the UI can show exact outcomes
// (matched/modified, already_banned, sessions_revoked, ...).
export const adminResetLeaderboard = (options = {}) =>
  requestWithCsrf("admin", "post", "/admin/leaderboard/reset", options);

export const adminSetLeaderboardVisibility = (userId, hidden) =>
  requestWithCsrf("admin", "patch", `/admin/leaderboard/users/${userId}`, {
    hidden: Boolean(hidden),
  });

export const adminBanUser = (userId, reason) =>
  requestWithCsrf("admin", "post", `/admin/users/${userId}/ban`, {
    ...(reason ? { reason } : {}),
  });

export const adminUnbanUser = (userId) =>
  requestWithCsrf("admin", "post", `/admin/users/${userId}/unban`);
